import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { FaChevronDown, FaCar, FaSearch, FaChevronRight } from "react-icons/fa";
import { useVehicle } from "../contexts/VehicleContext";
import { useThemeClasses } from "../hooks/useThemeClasses";
import { getVehicleMakers, getModelsForMaker, getYearsForModel, getModificationsForYear } from "../data/vehicleData";

const POPULAR = [
  { label: "Brake pads Maruti Swift", vehicleMake: "Maruti", vehicleModel: "Swift", search: "brake pads" },
  { label: "Air filter Honda City 2020", vehicleMake: "Honda", vehicleModel: "City", vehicleYear: "2020", search: "air filter" },
  { label: "Clutch plate Hyundai i20", vehicleMake: "Hyundai", vehicleModel: "i20", search: "clutch plate" },
  { label: "OEM 45022-TF0-G00", search: "45022-TF0-G00" },
  { label: "Shock absorber front", search: "shock absorber front" },
  { label: "Oil filter Tata Nexon", vehicleMake: "Tata", vehicleModel: "Nexon", search: "oil filter" },
];

function buildCatalogHref({ vehicleMake, vehicleModel, vehicleYear, vehicleVariant, vehicleFuelType, search }) {
  const p = new URLSearchParams();
  if (vehicleMake) p.set("vehicleMake", vehicleMake);
  if (vehicleModel) p.set("vehicleModel", vehicleModel);
  if (vehicleYear) p.set("vehicleYear", vehicleYear);
  if (vehicleVariant) p.set("vehicleVariant", vehicleVariant);
  if (vehicleFuelType) p.set("vehicleFuelType", vehicleFuelType);
  if (search) p.set("search", search);
  const qs = p.toString();
  return qs ? `/catalog?${qs}` : "/catalog";
}

function formatLabel(value) {
  return String(value ?? "").replace(/_/g, " ");
}

function VehicleField({ id, label, value, onChange, disabled, children }) {
  return (
    <div className="sp-vfield">
      <label htmlFor={id} className="sp-vfield__label">
        {label}
      </label>
      <div className="sp-vfield__wrap">
        <select
          id={id}
          className="sp-vfield__select catalog-car-maker-select"
          value={value}
          onChange={onChange}
          disabled={disabled}
        >
          {children}
        </select>
        <FaChevronDown className="sp-vfield__chev" aria-hidden />
      </div>
    </div>
  );
}

/**
 * Home hero strip: vehicle picker card + popular searches.
 */
export default function HomeVehiclePartsStrip() {
  const { selectedVehicle, selectVehicle, fetchMakes, fetchModels, fetchYears, fetchVariants } = useVehicle();
  const { isDark } = useThemeClasses();
  const sectionRef = useRef(null);

  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [years, setYears] = useState([]);
  const [variants, setVariants] = useState([]);

  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [year, setYear] = useState("");
  const [variant, setVariant] = useState("");
  const [transmissionType, setTransmissionType] = useState("");

  const currentMakerRef = useRef("");

  const syncVehicleToContext = useCallback(
    (next) => {
      selectVehicle({
        ...(selectedVehicle && typeof selectedVehicle === "object" ? selectedVehicle : {}),
        _id: selectedVehicle?._id,
        id: selectedVehicle?.id || selectedVehicle?._id || "home-vehicle",
        make: next.make ?? "",
        model: next.model ?? "",
        year: next.year ?? "",
        variant: next.variant ?? "",
        transmissionType: next.transmissionType ?? "",
        transmission: next.transmissionType ?? "",
      });
    },
    [selectVehicle, selectedVehicle]
  );

  useEffect(() => {
    const v = selectedVehicle;
    if (!v) return;
    const mk = v.make || v.vehicleMake || "";
    const md = v.model || v.vehicleModel || "";
    const yr = String(v.year || v.vehicleYear || "");
    const vr = v.variant || v.vehicleVariant || "";
    const tt = v.transmissionType || v.transmission || "";
    setMake(mk);
    setModel(md);
    setYear(yr);
    setVariant(vr);
    setTransmissionType(tt);
    currentMakerRef.current = mk;
  }, [selectedVehicle?._id, selectedVehicle?.id, selectedVehicle?.make, selectedVehicle?.model]);

  useEffect(() => {
    (async () => {
      try {
        const apiMakes = await fetchMakes?.();
        if (apiMakes && apiMakes.length > 0) {
          setMakes(apiMakes);
          return;
        }
      } catch {
        /* fallback */
      }
      setMakes(getVehicleMakers("car").map((m) => ({ value: m, label: formatLabel(m) })));
    })();
  }, [fetchMakes]);

  const handleMakeChange = async (e) => {
    const value = e.target.value;
    currentMakerRef.current = value;
    setMake(value);
    setModel("");
    setYear("");
    setVariant("");
    setModels([]);
    setYears([]);
    setVariants([]);

    if (!value) {
      syncVehicleToContext({ make: "", model: "", year: "", variant: "", transmissionType: "" });
      return;
    }

    try {
      const list = await fetchModels?.(value);
      if (list && list.length > 0) {
        setModels(list);
        syncVehicleToContext({ make: value, model: "", year: "", variant: "", transmissionType: transmissionType || "" });
        return;
      }
    } catch {
      /* local */
    }
    const local = getModelsForMaker(value, "car").map((m) => ({ value: m, label: formatLabel(m) }));
    setModels(local);
    syncVehicleToContext({ make: value, model: "", year: "", variant: "", transmissionType: transmissionType || "" });
  };

  const handleModelChange = async (e) => {
    const value = e.target.value;
    setModel(value);
    setYear("");
    setVariant("");
    setYears([]);
    setVariants([]);
    const maker = currentMakerRef.current || make;

    if (!maker || !value) {
      syncVehicleToContext({ make: maker, model: value, year: "", variant: "", transmissionType: transmissionType || "" });
      return;
    }

    try {
      const [yrList, vrList] = await Promise.all([
        fetchYears ? fetchYears(maker, value) : Promise.resolve([]),
        fetchVariants ? fetchVariants(maker, value) : Promise.resolve([]),
      ]);
      const normalizedVariants = (vrList || []).map((x) =>
        typeof x === "string" ? { value: x, label: x } : { value: x.value || x, label: x.label || x.value || x }
      );
      if (yrList && yrList.length > 0) {
        setYears(yrList);
        setVariants(normalizedVariants);
        syncVehicleToContext({ make: maker, model: value, year: "", variant: "", transmissionType: transmissionType || "" });
        return;
      }
    } catch {
      /* local years */
    }
    const localY = getYearsForModel(maker, value, "car").map((y) => ({ value: String(y), label: String(y) }));
    setYears(localY);
    setVariants([]);
    syncVehicleToContext({ make: maker, model: value, year: "", variant: "", transmissionType: transmissionType || "" });
  };

  const handleYearChange = (e) => {
    const value = e.target.value;
    setYear(value);
    setVariant("");
    const maker = currentMakerRef.current || make;
    if (variants.length === 0 && value && maker && model) {
      const localMods = getModificationsForYear(maker, model, value, "car");
      setVariants(localMods.map((x) => ({ value: x, label: x })));
    }
    syncVehicleToContext({ make: maker, model, year: value, variant: "", transmissionType: transmissionType || "" });
  };

  const handleVariantChange = (e) => {
    const value = e.target.value;
    setVariant(value);
    const maker = currentMakerRef.current || make;
    syncVehicleToContext({ make: maker, model, year, variant: value, transmissionType: transmissionType || "" });
  };

  const handleTransmissionTypeChange = (e) => {
    const value = e.target.value;
    setTransmissionType(value);
    const maker = currentMakerRef.current || make;
    syncVehicleToContext({ make: maker, model, year, variant, transmissionType: value });
  };

  const vehicleSummary = useMemo(() => {
    const parts = [formatLabel(make), formatLabel(model), year, variant].filter(Boolean);
    return parts.join(" · ");
  }, [make, model, year, variant]);

  const catalogHref = buildCatalogHref({
    vehicleMake: make,
    vehicleModel: model,
    vehicleYear: year,
    vehicleVariant: variant,
  });

  const canBrowse = Boolean(make && model);

  useEffect(() => {
    try {
      const shouldJump = sessionStorage.getItem("jumpToVehicleSection");
      if (shouldJump !== "1") return;
      sessionStorage.removeItem("jumpToVehicleSection");
      requestAnimationFrame(() => {
        const target = sectionRef.current || document.getElementById("find-right-parts");
        target?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    } catch {
      /* no-op */
    }
  }, []);

  return (
    <section
      id="find-right-parts"
      ref={sectionRef}
      className={`sp-vehicle-hero relative left-1/2 z-[1] w-screen max-w-[100vw] -translate-x-1/2 overflow-x-hidden px-4 sm:px-6 lg:px-10 ${
        isDark ? "" : "border-b border-gray-200 bg-slate-50"
      }`}
      aria-label="Vehicle and popular parts"
    >
      <div className="mx-auto w-full max-w-[min(100%,120rem)]">
        {isDark && (
          <div className="sp-vehicle-hero__head">
            <h1 className="sp-hero-h">
              Find the right <span className="accent">parts</span> for your vehicle
            </h1>
            <p className="sp-hero-sub mt-1.5">
              OEM, aftermarket &amp; used — search by vehicle, part number, or brand
            </p>
          </div>
        )}

        <div className="sp-vehicle-picker">
          <div className="sp-vehicle-picker__top">
            <div className="sp-vehicle-picker__icon" aria-hidden>
              <FaCar className="text-lg" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="sp-vehicle-picker__title">Select your vehicle</p>
              {vehicleSummary ? (
                <p className="sp-vehicle-picker__summary truncate">{vehicleSummary}</p>
              ) : (
                <p className="sp-vehicle-picker__sub">Choose make &amp; model to see compatible parts</p>
              )}
            </div>
          </div>

          <div className="sp-vehicle-picker__body">
            <div className="sp-vehicle-fields">
              <VehicleField id="vehicle-make" label="Make" value={make} onChange={handleMakeChange}>
                <option value="">Maruti, Honda, Hyundai…</option>
                {makes.map((m) => {
                  const val = m.value ?? m;
                  const lab = m.label ?? formatLabel(m.value ?? m);
                  return (
                    <option key={String(val)} value={val}>
                      {lab}
                    </option>
                  );
                })}
              </VehicleField>

              <VehicleField id="vehicle-model" label="Model" value={model} onChange={handleModelChange} disabled={!make}>
                <option value="">Select model</option>
                {models.map((m) => {
                  const val = m.value ?? m;
                  const lab = m.label ?? String(m.value ?? m);
                  return (
                    <option key={String(val)} value={val}>
                      {lab}
                    </option>
                  );
                })}
              </VehicleField>

              <VehicleField id="vehicle-year" label="Year" value={year} onChange={handleYearChange} disabled={!model}>
                <option value="">Select year</option>
                {years.map((y) => {
                  const val = y.value ?? y;
                  const lab = y.label ?? String(y.value ?? y);
                  return (
                    <option key={String(val)} value={val}>
                      {lab}
                    </option>
                  );
                })}
              </VehicleField>

              <VehicleField
                id="vehicle-variant"
                label="Variant"
                value={variant}
                onChange={handleVariantChange}
                disabled={!year}
              >
                <option value="">Engine / trim</option>
                {variants.map((v) => {
                  const val = v.value ?? v;
                  const lab = v.label ?? String(v.value ?? v);
                  return (
                    <option key={String(val)} value={val}>
                      {lab}
                    </option>
                  );
                })}
              </VehicleField>

              <VehicleField
                id="vehicle-transmission"
                label="Transmission"
                value={transmissionType}
                onChange={handleTransmissionTypeChange}
              >
                <option value="">Any</option>
                <option value="Automatic">Automatic</option>
                <option value="Manual">Manual</option>
              </VehicleField>
            </div>

            {canBrowse ? (
              <Link to={catalogHref} className="sp-vehicle-picker__cta">
                <FaSearch className="text-sm shrink-0" aria-hidden />
                Find compatible parts
                <FaChevronRight className="text-xs opacity-80 shrink-0" aria-hidden />
              </Link>
            ) : (
              <button type="button" className="sp-vehicle-picker__cta" disabled>
                <FaSearch className="text-sm shrink-0" aria-hidden />
                {!make ? "Select make to continue" : "Select model to continue"}
              </button>
            )}
          </div>
        </div>

        <div className="sp-popular-block">
          <p className="sp-popular-block__label">Trending searches</p>
          <div className="sp-popular-scroll">
            {POPULAR.map((item) => (
              <Link key={item.label} to={buildCatalogHref(item)} className="sp-popular-pill">
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
